import { useEffect, useState } from 'react';

interface MandatoryUpdateInfo {
  currentVersion: string;
  minVersion: string;
  latestVersion: string | null;
  message: string;
  isBelowMinVersion: boolean;
  isForceUpdateVersion: boolean;
}

interface DownloadProgress {
  bytesPerSecond: number;
  percent: number;
  transferred: number;
  total: number;
}

const MandatoryUpdateOverlay: React.FC = () => {
  const [mandatoryUpdateInfo, setMandatoryUpdateInfo] = useState<MandatoryUpdateInfo | null>(null);
  const [downloadProgress, setDownloadProgress] = useState<DownloadProgress | null>(null);
  const [isDownloading, setIsDownloading] = useState(false);
  const [updateDownloaded, setUpdateDownloaded] = useState(false);

  useEffect(() => {
    if (typeof window !== 'undefined' && window.electron?.isElectron) {
      const electron = window.electron;

      const cleanupMandatoryUpdate = electron.onMandatoryUpdateRequired?.((info: MandatoryUpdateInfo) => {
        setMandatoryUpdateInfo(info);
      });

      const cleanupProgress = electron.onDownloadProgress?.((progress: DownloadProgress) => {
        setDownloadProgress(progress);
      });

      const cleanupDownloadStart = electron.onUpdateStatus?.((data: any) => {
        if (data.status === 'downloading' && mandatoryUpdateInfo) {
          setIsDownloading(true);
          setDownloadProgress(null);
          setUpdateDownloaded(false);
        }
        if (data.status === 'downloaded' && mandatoryUpdateInfo) {
          setIsDownloading(false);
          setDownloadProgress(null);
          setUpdateDownloaded(true);
        }
      });

      return () => {
        cleanupMandatoryUpdate?.();
        cleanupProgress?.();
        cleanupDownloadStart?.();
      };
    }
  }, [mandatoryUpdateInfo]);

  useEffect(() => {
    const isActive = !!mandatoryUpdateInfo;

    if (isActive) {
      const preventKeyboardEvent = (e: KeyboardEvent) => {
        e.preventDefault();
        e.stopPropagation();
      };

      document.addEventListener('keydown', preventKeyboardEvent, true);
      document.addEventListener('keyup', preventKeyboardEvent, true);
      document.addEventListener('keypress', preventKeyboardEvent, true);

      return () => {
        document.removeEventListener('keydown', preventKeyboardEvent, true);
        document.removeEventListener('keyup', preventKeyboardEvent, true);
        document.removeEventListener('keypress', preventKeyboardEvent, true);
      };
    }
  }, [mandatoryUpdateInfo]);

  const handleUpdateNow = async () => {
    try {
      if (typeof window !== 'undefined' && window.electron?.downloadUpdate) {
        await window.electron.downloadUpdate();
      }
    } catch (error) {
      console.error('Failed to trigger mandatory update:', error);
    }
  };

  const handleRestart = async () => {
    try {
      if (typeof window !== 'undefined' && window.electron?.restartApp) {
        await window.electron.restartApp();
      }
    } catch (error) {
      console.error('Failed to restart app:', error);
    }
  };

  if (typeof window === 'undefined' || !window.electron?.isElectron || !mandatoryUpdateInfo) {
    return null;
  }

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-gray-900 bg-opacity-95"
      onClick={(e) => e.preventDefault()}
      onMouseDown={(e) => e.preventDefault()}
      onMouseUp={(e) => e.preventDefault()}
      style={{ pointerEvents: 'auto' }}
    >
      <div className="max-w-md w-full mx-4 bg-white rounded-lg shadow-2xl p-8">
        <div className="text-center">
          <div className="mx-auto flex items-center justify-center h-16 w-16 rounded-full bg-red-100 mb-4">
            <svg className="h-10 w-10 text-red-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
            </svg>
          </div>

          <h2 className="text-2xl font-bold text-gray-900 mb-2">
            Mandatory Update Required
          </h2>

          <p className="text-gray-600 mb-6">
            {mandatoryUpdateInfo.message}
          </p>

          <div className="bg-gray-50 rounded-md p-4 mb-6 text-left">
            <div className="text-sm text-gray-600">
              <div className="flex justify-between mb-1">
                <span>Current Version:</span>
                <span className="font-medium">{mandatoryUpdateInfo.currentVersion}</span>
              </div>
              <div className="flex justify-between mb-1">
                <span>Minimum Required:</span>
                <span className="font-medium">{mandatoryUpdateInfo.minVersion}</span>
              </div>
              {mandatoryUpdateInfo.latestVersion && (
                <div className="flex justify-between">
                  <span>Latest Available:</span>
                  <span className="font-medium">{mandatoryUpdateInfo.latestVersion}</span>
                </div>
              )}
            </div>
          </div>

          {downloadProgress && (
            <div className="mb-6">
              <div className="flex items-center justify-between mb-2">
                <span className="text-sm text-gray-600">Downloading update...</span>
                <span className="text-sm font-medium text-gray-900">
                  {Math.round(downloadProgress.percent)}%
                </span>
              </div>
              <div className="w-full bg-gray-200 rounded-full h-2">
                <div
                  className="bg-blue-600 h-2 rounded-full transition-all duration-300"
                  style={{ width: `${downloadProgress.percent}%` }}
                ></div>
              </div>
              <p className="text-xs text-gray-500 mt-2 text-center">
                Click to restart when download completes
              </p>
            </div>
          )}

          {isDownloading && !downloadProgress && (
            <div className="mb-6">
              <div className="flex items-center justify-center text-blue-600 mb-2">
                <svg className="h-6 w-6 mr-2 animate-spin" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
                <span className="font-medium">Starting download...</span>
              </div>
              <p className="text-xs text-gray-500 text-center">
                Click to restart when download completes
              </p>
            </div>
          )}

          {!downloadProgress && !isDownloading && !updateDownloaded && (
            <button
              onClick={handleUpdateNow}
              className="w-full px-4 py-3 bg-blue-600 text-white font-medium rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 transition-colors"
            >
              Update Now
            </button>
          )}

          {downloadProgress && (
            <button
              disabled
              className="w-full px-4 py-3 bg-gray-300 text-gray-500 font-medium rounded-md cursor-not-allowed"
            >
              Downloading...
            </button>
          )}

          {isDownloading && !downloadProgress && (
            <button
              disabled
              className="w-full px-4 py-3 bg-gray-300 text-gray-500 font-medium rounded-md cursor-not-allowed"
            >
              Starting download...
            </button>
          )}

          {updateDownloaded && (
            <button
              onClick={handleRestart}
              className="w-full px-4 py-3 bg-green-600 text-white font-medium rounded-md hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-green-500 focus:ring-offset-2 transition-colors"
            >
              Restart to Update
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default MandatoryUpdateOverlay;
